import ApiClient from "@/domain/core/utils/apiClient";
import client_core from "@/services/clients/client_core";

const client = new ApiClient(client_core);

const endpoints = {
    // Lista collaboratori (GET)
    list: '/dashboard/api/v3/collaborators/{scope}/collaborators/',

    // Creazione collaboratori (POST) - 3 endpoint separati
    invite: '/dashboard/api/v3/collaborators/{scope}/collaborators/invite/',
    assign: '/dashboard/api/v3/collaborators/{scope}/collaborators/assign/',
    virtual: '/dashboard/api/v3/collaborators/{scope}/collaborators/virtual/',

    // Operazioni su singolo collaboratore
    detail: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/',
    updateRoles: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/roles/',
    enable: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/enable/',
    resendInvitation: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/resend-invitation/',
    updateEmail: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/email/',
    forceAccept: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/force-accept/',
    permissions: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/permissions/',
    delete: '/dashboard/api/v3/collaborators/{scope}/collaborators/{id}/',

    // Ruoli
    roles: '/dashboard/api/v3/collaborators/{scope}/collaborators/roles/',
    rolePermissions: '/dashboard/api/v3/collaborators/{scope}/collaborators/roles/{roleId}/permissions/',

    // Ricerca utenti
    searchUsers: '/dashboard/api/v3/collaborators/{scope}/collaborators/search/',

    // Export FTP
    exportFtp: '/dashboard/api/v3/collaborators/{scope}/collaborators/export-ftp-credentials/',
};

const buildScope = (scope) => {
    if (scope.type === 'global') {
        return 'global';
    } else if (scope.type === 'contract-manager') {
        return `contract-manager/${scope.id}`;
    } else if (scope.type === 'pica-service') {
        return `pica-service/${scope.id}`;
    }
    throw new Error(`Invalid scope type: ${scope.type}`);
};

export default {
    // 1. Lista Collaboratori
    async fetchCollaborators(scope, params = {}) {
        const url = endpoints.list.replace('{scope}', buildScope(scope));
        const queryParams = {
            status: params.status,
            search: params.search,
            role_name: params.roleName,
            page: params.page || 1,
            page_size: params.pageSize || 20
        };
        
        // Remove undefined params
        Object.keys(queryParams).forEach(key => 
            queryParams[key] === undefined && delete queryParams[key]
        );
        
        return await client.get(url, queryParams);
    },

    // 2a. Invita Collaboratori (invia email di invito)
    async inviteCollaborators(scope, data) {
        const url = endpoints.invite.replace('{scope}', buildScope(scope));
        const payload = {
            users: data.users, // Array di email
            roles: data.roles
        };

        // Aggiungi personal_message se presente
        if (data.personalMessage) {
            payload.personal_message = data.personalMessage;
        }

        const response = await client.post(url, payload);
        // Response: {success: [...], errors: {...}}
        return response;
    },

    // 2b. Assegna Collaboratori (senza invito, pre-accettati)
    async assignCollaborators(scope, data) {
        const url = endpoints.assign.replace('{scope}', buildScope(scope));
        const payload = {
            users: data.users, // Array di email
            roles: data.roles
        };

        // Aggiungi personal_message se presente
        if (data.personalMessage) {
            payload.personal_message = data.personalMessage;
        }

        const response = await client.post(url, payload);
        // Response: {success: [...], errors: {...}}
        return response;
    },

    // 2c. Crea Virtual Users
    async createVirtualUsers(scope, data) {
        const url = endpoints.virtual.replace('{scope}', buildScope(scope));
        const payload = {
            users: data.users, // Array di username
            roles: data.roles
        };

        const response = await client.post(url, payload);
        // Response: {collaborators: [...], credentials: [...], errors: {...}, success_count: N, error_count: N}
        return response;
    },

    // 3. Aggiorna Ruoli
    async updateCollaboratorRoles(scope, collaboratorId, roles) {
        const url = endpoints.updateRoles
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.put(url, { roles });
    },

    // 4. Disabilita Collaboratore
    async disableCollaborator(scope, collaboratorId) {
        const url = endpoints.delete
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.delete(url);
    },

    // 5. Abilita Collaboratore
    async enableCollaborator(scope, collaboratorId) {
        const url = endpoints.enable
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.post(url);
    },

    // 6. Reinvia Invito
    async resendInvitation(scope, collaboratorId) {
        const url = endpoints.resendInvitation
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.post(url);
    },

    // 7. Aggiorna Email Collaboratore
    async updateCollaboratorEmail(scope, collaboratorId, email) {
        const url = endpoints.updateEmail
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.patch(url, { email });
    },

    // 8. Forza Accettazione Invito
    async forceAcceptInvitation(scope, collaboratorId) {
        const url = endpoints.forceAccept
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.post(url);
    },

    // 9. Dettaglio Collaboratore (include ftp_credentials)
    async getCollaboratorDetail(scope, collaboratorId) {
        const url = endpoints.detail
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.get(url);
    },

    // 10. Permessi Aggregati Utente
    async getCollaboratorPermissions(scope, collaboratorId) {
        const url = endpoints.permissions
            .replace('{scope}', buildScope(scope))
            .replace('{id}', collaboratorId);
        return await client.get(url);
    },

    // 10. Lista Ruoli Assegnabili
    async fetchAvailableRoles(scope, params = {}) {
        const url = endpoints.roles.replace('{scope}', buildScope(scope));
        const queryParams = {
            role_type: params.roleType,
            search: params.search
        };
        
        // Remove undefined params
        Object.keys(queryParams).forEach(key => 
            queryParams[key] === undefined && delete queryParams[key]
        );
        
        return await client.get(url, queryParams);
    },

    // 11. Permessi per Ruolo
    async getRolePermissions(scope, roleId) {
        const url = endpoints.rolePermissions
            .replace('{scope}', buildScope(scope))
            .replace('{roleId}', roleId);
        const response = await client.get(url);
        
        // Restituisci direttamente la struttura dei permessi
        return response?.permissions || {};
    },
    
    // 12. Ricerca Utenti per Autocomplete
    async searchUsers(scope, searchTerm) {
        const url = endpoints.searchUsers.replace('{scope}', buildScope(scope));
        const response = await client.get(url, {
            q: searchTerm,
            limit: 10,
            exclude_existing: true // Esclude utenti già collaboratori in questo scope
        });
        // Il backend ritorna {results: [...]}
        return response;
    },

    // 13. Export Credenziali FTP
    async exportFtpCredentials(scope, {type, gallery_id}) {
        const url = endpoints.exportFtp.replace('{scope}', buildScope(scope));
        return await client.download(url, {}, {
            type,
            gallery_id
        }, 'POST');
    },

    // 14. Virtual Users - Reset Multiple Passwords (Batch)
    async resetAllVirtualUserPasswords(scope, collaboratorIds = []) {
        const url = `/dashboard/api/v3/collaborators/${buildScope(scope)}/collaborators/reset-all-virtual-passwords/`;
        return await client.post(url, {
            collaborator_ids: collaboratorIds
        });
    }
};